import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, Form, Select, Button, DatePicker, Switch, Radio, message, Divider, Tag, Alert, Popconfirm, Space, Modal } from "antd";
import Table from 'components/Common/Table';
import { PlayCircleOutlined, CheckCircleOutlined, ExclamationCircleOutlined, EyeOutlined } from "@ant-design/icons";
import * as adminactions from "store/actions/AuthActions";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import PayrollProgress from "components/Payroll/PayrollProgress";

const { Option } = Select;
const { MonthPicker } = DatePicker;

const RunPayroll = () => {
  const [form] = Form.useForm();
  const [runType, setRunType] = useState("company");
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [skippedEmployees, setSkippedEmployees] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [taskId, setTaskId] = useState(null);
  const [taskData, setTaskData] = useState(null);
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [isDryRun, setIsDryRun] = useState(true); // Default to dry run
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [currentRunId, setCurrentRunId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);

  const pollingInterval = useRef(null);
  const organization = useSelector((state) => state.auth.organization);

  const resultColumns = [
    {
      title: "Employee",
      dataIndex: ["employee", "name"],
      key: "employee",
      render: (text, record) => text || `${record.employee_first_name} ${record.employee_last_name}`,
    },
    {
      title: "Employee Code",
      dataIndex: ["employee", "employee_id"],
      key: "employee_code",
      render: (text, record) => text || record.employee_code,
    },
    {
      title: "Department",
      dataIndex: ["employee", "department", "name"],
      key: "department",
      render: (text, record) => text || record.department_name || "-",
    },
    {
      title: "Working Days",
      dataIndex: "total_working_days",
      key: "total_working_days",
      align: "right",
      render: (val) => val || "-",
    },
    {
      title: "Effective Days",
      dataIndex: "effective_days",
      key: "effective_days",
      align: "right",
      render: (val) => val || "-",
    },
    {
      title: "Attendance",
      dataIndex: "is_attendance_considered",
      key: "is_attendance_considered",
      align: "center",
      render: (val) => val ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>,
    },
    {
      title: "Basic Salary",
      dataIndex: "basic_salary",
      key: "basic_salary",
      align: "right",
      render: (amount) => amount ? `₹${parseFloat(amount).toFixed(2)}` : "-",
    },
    {
      title: "Total Allowances",
      key: "total_allowances",
      align: "right",
      render: (_, record) => {
        const totalEarnings = parseFloat(record.total_earnings || 0);
        const basicSalary = parseFloat(record.basic_salary || 0);
        const allowances = totalEarnings - basicSalary;
        return allowances > 0 ? `₹${allowances.toFixed(2)}` : "-";
      },
    },
    {
      title: "Gross Salary",
      dataIndex: "gross_salary",
      key: "gross_salary",
      align: "right",
      render: (amount) => amount ? `₹${parseFloat(amount).toFixed(2)}` : "-",
    },
    {
      title: "Total Deductions",
      dataIndex: "total_deductions",
      key: "total_deductions",
      align: "right",
      render: (amount) => amount ? `₹${parseFloat(amount).toFixed(2)}` : "-",
    },
    {
      title: "Net Salary",
      dataIndex: "net_salary",
      key: "net_salary",
      align: "right",
      render: (amount) => amount ? `₹${parseFloat(amount).toFixed(2)}` : "-",
    },
    {
      title: "Rounded Off",
      dataIndex: "net_salary",
      key: "net_salary_rounded",
      align: "right",
      render: (amount) => amount ? `₹${Math.round(parseFloat(amount)).toLocaleString()}` : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        if (record.already_processed) {
          return <Tag color="orange" icon={<ExclamationCircleOutlined />}>Already Processed</Tag>;
        }
        return <Tag color="green" icon={<CheckCircleOutlined />}>{status || "PROCESSED"}</Tag>;
      },
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 80,
      render: (_, record) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          size="small"
          onClick={() => handleViewPayroll(record)}
        >
          View
        </Button>
      ),
    },
  ];

  const handleViewPayroll = (record) => {
    setSelectedPayroll(record);
    setViewModalVisible(true);
  };

  const renderEarningsBreakdown = (breakdown, ytdBreakdown = null) => {
    if (!breakdown || Object.keys(breakdown).length === 0) return "-";
    return (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: 4 }}>
          <span style={{ flex: 2 }}>Component</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Amount</span>
          {ytdBreakdown && <span style={{ flex: 1, textAlign: 'right' }}>YTD</span>}
        </div>
        {Object.entries(breakdown).map(([key, value]) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ flex: 2 }}>{key}</span>
            <span style={{ flex: 1, textAlign: 'right', fontFamily: 'monospace' }}>₹{parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            {ytdBreakdown && (
              <span style={{ flex: 1, textAlign: 'right', fontFamily: 'monospace', color: '#1890ff' }}>
                ₹{parseFloat(ytdBreakdown[key] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderDeductionsBreakdown = (breakdown, ytdBreakdown = null) => {
    if (!breakdown || Object.keys(breakdown).length === 0) return "-";
    return (
      <div style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: 4 }}>
          <span style={{ flex: 2 }}>Component</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Amount</span>
          {ytdBreakdown && <span style={{ flex: 1, textAlign: 'right' }}>YTD</span>}
        </div>
        {Object.entries(breakdown).map(([key, value]) => (
          <div key={key} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={{ flex: 2 }}>{key}</span>
            <span style={{ flex: 1, textAlign: 'right', color: '#ff4d4f', fontFamily: 'monospace' }}>₹{parseFloat(value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            {ytdBreakdown && (
              <span style={{ flex: 1, textAlign: 'right', fontFamily: 'monospace', color: '#cf1322' }}>
                ₹{parseFloat(ytdBreakdown[key] || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    loadCompanies();

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, []);

  // Load employees and departments when company changes
  useEffect(() => {
    if (selectedCompany) {
      loadEmployees(selectedCompany);
      loadDepartments(selectedCompany);
    } else {
      setEmployees([]);
      setDepartments([]);
    }
    // Clear employee/department selections when company changes
    form.setFieldsValue({ employees: [], departments: [] });
  }, [selectedCompany]);

  const loadCompanies = async () => {
    try {
      // Load organizations - assuming there's an API for this
      const response = await adminactions.loadOrganizations();
      if (response) {
        setCompanies(Array.isArray(response) ? response : [response]);
      }
    } catch (error) {
      console.error("Error loading companies:", error);
      // Fallback to current organization
      if (organization) {
        setCompanies([{ id: organization, name: "Current Organization" }]);
      }
    }
  };

  const loadDepartments = async (companyId) => {
    try {
      const response = await adminactions.loadDepartmentsByOrganization(companyId);
      if (response) {
        setDepartments(response);
      }
    } catch (error) {
      console.error("Error loading departments:", error);
    }
  };

  const loadEmployees = async (companyId) => {
    try {
      const response = await adminactions.loadEmployeesByOrganization(companyId, 1, undefined, 1000);
      if (response) {
        setEmployees(response.results || []);
      }
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const startPolling = (taskIdToCheck, runId) => {
    if (pollingInterval.current) {
      clearInterval(pollingInterval.current);
    }

    pollingInterval.current = setInterval(async () => {
      const progressData = await adminactions.checkPayrollProgress(taskIdToCheck);

      if (progressData) {
        setTaskData(progressData);

        if (progressData.status === "COMPLETED") {
          clearInterval(pollingInterval.current);
          setProcessing(false);

          if (progressData.is_dry_run) {
            message.success("Dry run completed! Review the results below and confirm to process payroll.");
          } else {
            message.success("Payroll processing completed!");
          }

          // Load results if available
          const actualRunId = runId || progressData.run_id;
          if (actualRunId) {
            setCurrentRunId(actualRunId);
            const resultsData = await adminactions.loadPayrollResults(actualRunId);
            if (resultsData) {
              const resultsList = resultsData.results || resultsData;
              setResults(resultsList);
              setShowResults(true);
              // Pre-select all employees that are not already processed
              const selectableKeys = resultsList
                .filter(r => !r.already_processed)
                .map(r => r.id);
              setSelectedRowKeys(selectableKeys);
            }
          }
        } else if (progressData.status === "FAILED" || progressData.status === "CANCELLED") {
          clearInterval(pollingInterval.current);
          setProcessing(false);
          if (progressData.status === "FAILED") {
            message.error("Payroll processing failed!");
          }
        }
      }
    }, 1000); // Poll every second
  };

  const handleRunPayroll = async (values) => {
    try {
      setProcessing(true);
      setShowResults(false);
      setResults([]);
      setTaskData(null);
      setCurrentRunId(null);
      setSelectedRowKeys([]);
      setSkippedEmployees([]);

      const payload = {
        company: values.company || organization,
        run_type: runType,
        period: values.period.endOf('month').format("YYYY-MM-DD"),
        use_precalculated_attendance: values.usePrecalculated || false,
        is_dry_run: isDryRun,
      };

      if (runType === "department" && values.departments) {
        payload.departments = values.departments;
      } else if (runType === "individual" && values.employees) {
        payload.employees = values.employees;
      }

      const response = await adminactions.runPayroll(payload);

      if (response?.skipped_employees?.length) {
        setSkippedEmployees(response.skipped_employees);
      }

      if (response && response.task_id) {
        setTaskId(response.task_id);
        setTaskData({ status: "PROCESSING", progress: 0, is_dry_run: isDryRun });
        message.info(isDryRun ? "Starting dry run..." : "Payroll processing started...");
        startPolling(response.task_id, response.run_id);
      }
    } catch (error) {
      console.error("Error running payroll:", error);
      // The 400 for "everyone selected had already left" carries the same
      // structured list, so show who was skipped rather than only the toast.
      const skipped = error?.response?.data?.skipped_employees;
      if (skipped?.length) {
        setSkippedEmployees(skipped);
      }
      setProcessing(false);
    }
  };

  const handleConfirmPayroll = async () => {
    if (!currentRunId) {
      message.error("No payroll run to confirm");
      return;
    }

    try {
      setConfirming(true);

      // Get excluded employees (those not selected)
      const allEmployeeIds = results.map(r => r.employee);
      const selectedEmployeeIds = results
        .filter(r => selectedRowKeys.includes(r.id))
        .map(r => r.employee);
      const excludeEmployees = allEmployeeIds.filter(id => !selectedEmployeeIds.includes(id));

      const response = await adminactions.confirmPayroll({
        run_id: currentRunId,
        exclude_employees: excludeEmployees,
      });

      if (response) {
        message.success(`Payroll confirmed! ${response.saved_count} records saved, ${response.skipped_count} skipped.`);
        setShowResults(false);
        setResults([]);
        setCurrentRunId(null);
        setTaskData(null);
      }
    } catch (error) {
      console.error("Error confirming payroll:", error);
      message.error("Failed to confirm payroll");
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelTask = async () => {
    if (taskId) {
      await adminactions.cancelPayrollTask(taskId);
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
      setProcessing(false);
      setTaskData({ ...taskData, status: "CANCELLED" });
    }
  };

  const disabledDate = (current) => {
    // Can't select future months
    return current && current > dayjs().endOf("month");
  };

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={10}>
          <Card title="Run Payroll Configuration">
            {skippedEmployees.length > 0 && (
              <Alert
                message="Some employees were skipped"
                description={
                  <>
                    <div>
                      Their last working day falls before this payroll period started,
                      so there is nothing to pay them for this period.
                    </div>
                    <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                      {skippedEmployees.map((emp) => (
                        <li key={emp.id}>
                          {emp.name} ({emp.employee_code}) — left{" "}
                          {dayjs(emp.last_working_day).format("DD-MMM-YYYY")}
                        </li>
                      ))}
                    </ul>
                  </>
                }
                type="warning"
                showIcon
                closable
                onClose={() => setSkippedEmployees([])}
                style={{ marginBottom: 16 }}
              />
            )}
            <Form
              form={form}
              layout="vertical"
              onFinish={handleRunPayroll}
              initialValues={{
                runType: "company",
                usePrecalculated: false,
              }}
            >
              <Form.Item
                label="Company"
                name="company"
                rules={[{ required: true, message: "Please select a company" }]}
              >
                <Select
                  placeholder="Select company"
                  onChange={(value) => setSelectedCompany(value)}
                >
                  {companies.map((company) => (
                    <Option key={company.id} value={company.id}>
                      {company.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Payroll Run Type"
                name="runType"
              >
                <Radio.Group onChange={(e) => setRunType(e.target.value)} value={runType}>
                  <Radio value="company">Company-wide</Radio>
                  <Radio value="department">Department-wise</Radio>
                  <Radio value="individual">Individual Employee</Radio>
                </Radio.Group>
              </Form.Item>

              {runType === "department" && (
                <Form.Item
                  label="Departments"
                  name="departments"
                  rules={[{ required: true, message: "Please select at least one department" }]}
                >
                  <Select
                    mode="multiple"
                    placeholder={selectedCompany ? "Select departments" : "Select a company first"}
                    disabled={!selectedCompany}
                    showSearch
                    filterOption={(input, option) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {departments.map((dept) => (
                      <Option key={dept.id} value={dept.id}>
                        {dept.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {runType === "individual" && (
                <Form.Item
                  label="Employees"
                  name="employees"
                  rules={[{ required: true, message: "Please select at least one employee" }]}
                >
                  <Select
                    mode="multiple"
                    placeholder={selectedCompany ? "Select employees" : "Select a company first"}
                    disabled={!selectedCompany}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                      const label = Array.isArray(option?.children)
                        ? option.children.flat(Infinity).join('')
                        : String(option?.children ?? '');
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                  >
                    {employees.map((emp) => (
                      <Option key={emp.id} value={emp.id}>
                        {emp.user.first || `${emp.user.first_name || ''} ${emp.user.last_name || ''}`.trim()} ({emp.employee_id})
                        {emp.last_working_day
                          ? ` · Left ${dayjs(emp.last_working_day).format("DD-MMM-YYYY")}`
                          : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              <Form.Item
                label="Period"
                name="period"
                rules={[{ required: true, message: "Please select a period" }]}
              >
                <MonthPicker
                  style={{ width: "100%" }}
                  format="MMM YYYY"
                  disabledDate={disabledDate}
                  placeholder="Select month"
                />
              </Form.Item>

              <Form.Item
                label="Use Precalculated Attendance"
                name="usePrecalculated"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>

              <Divider />

              <Form.Item
                label="Dry Run (Preview Only)"
                help="When enabled, payroll will be calculated but NOT saved. You can review the results and then confirm to process."
              >
                <Switch
                  checked={isDryRun}
                  onChange={(checked) => setIsDryRun(checked)}
                  checkedChildren={<EyeOutlined />}
                  unCheckedChildren={<PlayCircleOutlined />}
                />
                <span style={{ marginLeft: 8 }}>
                  {isDryRun ? (
                    <Tag color="blue">Preview Mode</Tag>
                  ) : (
                    <Tag color="green">Direct Processing</Tag>
                  )}
                </span>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={isDryRun ? <EyeOutlined /> : <PlayCircleOutlined />}
                  loading={processing}
                  block
                  size="large"
                >
                  {processing ? "Processing..." : isDryRun ? "Preview Payroll (Dry Run)" : "Run Payroll"}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          {taskData && (
            <PayrollProgress
              taskData={taskData}
              onCancel={handleCancelTask}
            />
          )}

          {showResults && results.length > 0 && (
            <>
              <Divider />
              <Card
                title={
                  <Space>
                    <span>Payroll Results</span>
                    {taskData?.is_dry_run && (
                      <Tag color="blue">Dry Run - Not Saved</Tag>
                    )}
                  </Space>
                }
                extra={
                  taskData?.is_dry_run && currentRunId && (
                    <Space>
                      <span style={{ color: '#666' }}>
                        Selected: {selectedRowKeys.length} / {results.filter(r => !r.already_processed).length} employees
                      </span>
                      <Popconfirm
                        title="Confirm Payroll"
                        description={`Are you sure you want to process payroll for ${selectedRowKeys.length} selected employees?`}
                        onConfirm={handleConfirmPayroll}
                        okText="Yes, Process"
                        cancelText="Cancel"
                      >
                        <Button
                          type="primary"
                          icon={<CheckCircleOutlined />}
                          loading={confirming}
                          disabled={selectedRowKeys.length === 0}
                        >
                          Confirm & Process Payroll
                        </Button>
                      </Popconfirm>
                    </Space>
                  )
                }
              >
                {taskData?.is_dry_run && (
                  <Alert
                    message="Dry Run Preview"
                    description="This is a preview of the payroll. No records have been saved yet. Review the results and click 'Confirm & Process Payroll' to save. Employees marked as 'Already Processed' will be skipped."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                {results.some(r => r.already_processed) && (
                  <Alert
                    message="Some employees already have payroll for this month"
                    description="Employees marked in orange have already been processed for this month and will be skipped."
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}
                <style>{`.payroll-results-table .ant-table-thead > tr > th { white-space: nowrap; }`}</style>
                <Table
                  className="payroll-results-table"
                  columns={resultColumns}
                  dataSource={results}
                  rowKey="id"
                  pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                  scroll={{ x: 1800 }}
                  rowSelection={
                    taskData?.is_dry_run
                      ? {
                          selectedRowKeys,
                          onChange: (keys) => setSelectedRowKeys(keys),
                          getCheckboxProps: (record) => ({
                            disabled: record.already_processed,
                          }),
                        }
                      : undefined
                  }
                />
              </Card>
            </>
          )}
        </Col>
      </Row>

      {/* Payroll Detail Modal */}
      <Modal
        title={
          selectedPayroll ?
            `Payroll Details - ${selectedPayroll.employee?.name || `${selectedPayroll.employee_first_name} ${selectedPayroll.employee_last_name}`}` :
            'Payroll Details'
        }
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {selectedPayroll && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <div>
                <p><strong>Employee Name:</strong> {selectedPayroll.employee?.name || `${selectedPayroll.employee_first_name} ${selectedPayroll.employee_last_name}`}</p>
                <p><strong>Employee Code:</strong> {selectedPayroll.employee?.employee_id || selectedPayroll.employee_code}</p>
                <p><strong>Department:</strong> {selectedPayroll.employee?.department?.name || selectedPayroll.department_name || '-'}</p>
                <p><strong>Designation:</strong> {selectedPayroll.employee?.designation || selectedPayroll.designation || '-'}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p><strong>Working Days:</strong> {selectedPayroll.total_working_days || '-'}</p>
                <p><strong>Effective Days:</strong> {selectedPayroll.effective_days || '-'}</p>
                <p><strong>Days Present:</strong> {selectedPayroll.days_present || '-'}</p>
                <p><strong>Comp-off Credited:</strong> {selectedPayroll.comp_off_credited ?? '-'}</p>
                <p><strong>Attendance Considered:</strong> {selectedPayroll.is_attendance_considered ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>}</p>
              </div>
            </div>

            <Divider />

            <div style={{ display: 'flex', gap: 16 }}>
              <Card size="small" title="Earnings Breakdown" style={{ flex: 1 }} headStyle={{ backgroundColor: '#f6ffed' }}>
                {renderEarningsBreakdown(selectedPayroll.earnings_breakdown, selectedPayroll.ytd_earnings_breakdown)}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontWeight: 'bold' }}>
                  <span style={{ flex: 2 }}>Total Earnings:</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>₹{parseFloat(selectedPayroll.total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  {selectedPayroll.ytd_total_earnings !== undefined && (
                    <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedPayroll.ytd_total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  )}
                </div>
              </Card>

              <Card size="small" title="Deductions Breakdown" style={{ flex: 1 }} headStyle={{ backgroundColor: '#fff2e8' }}>
                {renderDeductionsBreakdown(selectedPayroll.deductions_breakdown, selectedPayroll.ytd_deductions_breakdown)}
                <Divider style={{ margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontWeight: 'bold' }}>
                  <span style={{ flex: 2 }}>Total Deductions:</span>
                  <span style={{ flex: 1, textAlign: 'right', color: '#ff4d4f' }}>₹{parseFloat(selectedPayroll.total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  {selectedPayroll.ytd_total_deductions !== undefined && (
                    <span style={{ flex: 1, textAlign: 'right', color: '#cf1322' }}>₹{parseFloat(selectedPayroll.ytd_total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  )}
                </div>
              </Card>
            </div>

            <Divider />

            <Card size="small" style={{ backgroundColor: '#f0f9ff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontWeight: 'bold', borderBottom: '1px solid #ddd', paddingBottom: 4 }}>
                <span style={{ flex: 2 }}>Summary</span>
                <span style={{ flex: 1, textAlign: 'right' }}>This Month</span>
                {selectedPayroll.ytd_net_salary !== undefined && <span style={{ flex: 1, textAlign: 'right' }}>YTD</span>}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ flex: 2 }}><strong>Basic Salary:</strong></span>
                <span style={{ flex: 1, textAlign: 'right' }}>₹{parseFloat(selectedPayroll.basic_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedPayroll.ytd_basic_salary !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedPayroll.ytd_basic_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ flex: 2 }}><strong>Total Earnings:</strong></span>
                <span style={{ flex: 1, textAlign: 'right' }}>₹{parseFloat(selectedPayroll.total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedPayroll.ytd_total_earnings !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedPayroll.ytd_total_earnings || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ flex: 2 }}><strong>Total Deductions:</strong></span>
                <span style={{ flex: 1, textAlign: 'right', color: '#ff4d4f' }}>₹{parseFloat(selectedPayroll.total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedPayroll.ytd_total_deductions !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#cf1322' }}>₹{parseFloat(selectedPayroll.ytd_total_deductions || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '18px', fontWeight: 'bold' }}>
                <span style={{ flex: 2 }}>Net Salary:</span>
                <span style={{ flex: 1, textAlign: 'right', color: '#1890ff' }}>₹{parseFloat(selectedPayroll.net_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                {selectedPayroll.ytd_net_salary !== undefined && (
                  <span style={{ flex: 1, textAlign: 'right', color: '#52c41a' }}>₹{parseFloat(selectedPayroll.ytd_net_salary || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', marginTop: 4 }}>
                <span style={{ flex: 2 }}><strong>Rounded Off:</strong></span>
                <span style={{ flex: 1, textAlign: 'right', color: '#1890ff', fontWeight: 'bold' }}>₹{Math.round(parseFloat(selectedPayroll.net_salary || 0)).toLocaleString('en-IN')}</span>
                {selectedPayroll.ytd_net_salary !== undefined && <span style={{ flex: 1 }}></span>}
              </div>
            </Card>

            {selectedPayroll.already_processed && (
              <Alert
                message="Already Processed"
                description="This employee's payroll has already been processed for this month."
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RunPayroll;
